import React from 'react';
import {observer} from 'mobx-react';
import {TechseeBaseComponent} from '@techsee/techsee-ui-common/lib/_shared/techsee-base-component';
import {PrimaryButton} from '@techsee/techsee-ui-common/lib/forms/buttons/primary';
import {ButtonTypeAttributes} from '@techsee/techsee-ui-common/lib/forms/buttons/generic';
import {Spinner} from '@techsee/techsee-ui-common/lib/spinner';
import {SimpleLabel} from '@techsee/techsee-ui-common/lib/forms/labels/simple';
import {Datepicker} from '@techsee/techsee-ui-common/lib/datepicker';
import {IHistoryConsoleFilterController, QUICK_DATE_RANGES} from './controller';

import './styles.scss';

export interface IHistoryConsoleFilter {
    ctrl?: IHistoryConsoleFilterController;
}

@observer
export class HistoryConsoleFilter extends TechseeBaseComponent<IHistoryConsoleFilter> {
    componentWillUnmount(): void {
        this.props.ctrl?.clearForm();
    }

    renderInternal() {
        const {ctrl} = this.props;
        if (!ctrl) {
            return <></>;
        }
        const {filterState, translate} = ctrl;

        return (
            <div className='history-console-filter'>
                <SimpleLabel className='filter-title'>{translate('REACT.HISTORY_CONSOLE.FILTER.TITLE')}</SimpleLabel>

                <form className='use-common-styles filter-form' onSubmit={(event) => ctrl.onSubmit(event)}>
                    <div className='filter-section'>
                        <SimpleLabel className='section-label'>
                            {translate('REACT.HISTORY_CONSOLE.FILTER.CUSTOMER_IDENTIFIER')}
                        </SimpleLabel>

                        <div className='filter-field'>
                            <input
                                className='form-control filter-input'
                                type='text'
                                placeholder={translate('REACT.HISTORY_CONSOLE.FILTER.CUSTOMER_IDENTIFIER_PLACEHOLDER')}
                                value={filterState.customerIdentifier}
                                onChange={(e) => ctrl.setCustomerIdentifier(e.target.value)}
                                disabled={!ctrl.canUseExclusiveField('customerRef')}
                            />
                        </div>
                    </div>

                    <div className='filter-section date-section'>
                        <SimpleLabel className='section-label'>
                            {translate('REACT.HISTORY_CONSOLE.FILTER.DATE_RANGE')}
                        </SimpleLabel>

                        <div className='date-fields'>
                            <div className='date-field'>
                                <Datepicker
                                    dateFormat={ctrl.dateFormat}
                                    onDateChange={(date: Date) => ctrl.setStartDate(date?.getTime() || 0)}
                                    placeholderText={translate('REACT.HISTORY_CONSOLE.FILTER.START_DATE_PLACEHOLDER')}
                                    maxDate={ctrl.maxDate}
                                />
                            </div>
                            <span className='date-separator'>-</span>
                            <div className='date-field'>
                                <Datepicker
                                    dateFormat={ctrl.dateFormat}
                                    onDateChange={(date: Date) => ctrl.setEndDate(date?.getTime() || 0)}
                                    placeholderText={translate('REACT.HISTORY_CONSOLE.FILTER.END_DATE_PLACEHOLDER')}
                                    maxDate={ctrl.maxDate}
                                />
                            </div>
                        </div>

                        <div className='quick-actions'>
                            <span className='quick-actions-label'>
                                {translate('REACT.HISTORY_CONSOLE.FILTER.QUICK_ACTION_LABEL')}
                            </span>
                            <div className='quick-action-buttons'>
                                {QUICK_DATE_RANGES.map((range) => (
                                    <button
                                        key={range}
                                        type='button'
                                        className={`quick-action-btn${filterState.activeQuickFilter === range ? ' active' : ''}`}
                                        onClick={() => ctrl.setQuickDateRange(range)}>
                                        {translate(`REACT.HISTORY_CONSOLE.FILTER.QUICK_${range.toUpperCase()}`)}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className='filter-divider'>
                        <div className='divider-line'></div>
                        <span className='divider-text'>{translate('REACT.INVITE.VIEW.SECTION_INPUT_LABEL')}</span>
                        <div className='divider-line'></div>
                    </div>

                    <div className='filter-section'>
                        <SimpleLabel className='section-label'>
                            {translate('REACT.HISTORY_CONSOLE.FILTER.OPTIONAL_FILTERS')}
                        </SimpleLabel>

                        <div className='direct-lookup-fields'>
                            <div className='filter-field'>
                                <input
                                    className='form-control filter-input'
                                    type='text'
                                    placeholder={translate('REACT.HISTORY_CONSOLE.FILTER.SESSION_ID_PLACEHOLDER')}
                                    value={filterState.roomId}
                                    onChange={(e) => ctrl.setRoomId(e.target.value)}
                                    disabled={!ctrl.canUseExclusiveField('roomId')}
                                />
                            </div>

                            <div className='filter-field'>
                                <input
                                    className='form-control filter-input'
                                    type='text'
                                    inputMode='numeric'
                                    pattern='[0-9]*'
                                    placeholder={translate(
                                        'REACT.HISTORY_CONSOLE.FILTER.PRE_DATA_COLLECTION_PLACEHOLDER'
                                    )}
                                    value={filterState.preDataCollectionCode}
                                    onChange={(e) => ctrl.setPreDataCollectionCode(e.target.value.replace(/\D/g, ''))}
                                    disabled={!ctrl.canUseExclusiveField('preDataCollectionCode')}
                                />
                            </div>
                        </div>
                    </div>

                    {filterState.dateError && (
                        <div className='filter-error'>{translate('REACT.HISTORY_CONSOLE.FILTER.DATE_ERROR')}</div>
                    )}

                    {filterState.formError && (
                        <div className='filter-error'>{translate('REACT.HISTORY_CONSOLE.FILTER.ERROR')}</div>
                    )}

                    {filterState.searchError && (
                        <div className='filter-error'>{translate(filterState.searchError)}</div>
                    )}

                    <PrimaryButton
                        className={filterState.isLoading ? 'loading-button' : 'search-button'}
                        type={ButtonTypeAttributes.Submit}
                        disabled={filterState.isLoading || !ctrl.isFormValid}>
                        {filterState.isLoading ? <Spinner /> : <span>{translate('INVITE.VIEW.SEARCH')}</span>}
                    </PrimaryButton>
                </form>
            </div>
        );
    }
}
